import type { CashflowItem } from './cashflowApi';

/**
 * Day-by-day view of the cash-flow projection ("Pe zile"): the projection's
 * items of ONE currency grouped per calendar day, with the running balance
 * carried forward from the current bank balance of that currency. Days
 * without movements are skipped. Pure — shared by the web and mobile pages.
 */
export interface CashflowDay {
  /** Calendar day, `YYYY-MM-DD` (UTC, like the item dates). */
  date: string;
  inflow: number;
  outflow: number;
  net: number;
  /** Running balance at the end of the day; null when the currency has no known bank balance. */
  balance: number | null;
  items: CashflowItem[];
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function buildCashflowDays(
  items: CashflowItem[],
  openingBalance: number | null,
): CashflowDay[] {
  const byDay = new Map<string, CashflowItem[]>();
  for (const item of items) {
    const key = item.date.slice(0, 10);
    const list = byDay.get(key);
    if (list) list.push(item);
    else byDay.set(key, [item]);
  }
  const days: CashflowDay[] = [];
  let running = openingBalance;
  for (const date of [...byDay.keys()].sort()) {
    const dayItems = byDay.get(date) as CashflowItem[];
    // Receipts first, then payments, biggest amounts first — reads like the
    // "what happens" column of a cash-flow sheet.
    dayItems.sort((a, b) =>
      a.direction !== b.direction ? (a.direction === 'in' ? -1 : 1) : b.amount - a.amount,
    );
    let inflow = 0;
    let outflow = 0;
    for (const item of dayItems) {
      if (item.direction === 'in') inflow += item.amount;
      else outflow += item.amount;
    }
    const net = round2(inflow - outflow);
    if (running !== null) running = round2(running + net);
    days.push({
      date,
      inflow: round2(inflow),
      outflow: round2(outflow),
      net,
      balance: running,
      items: dayItems,
    });
  }
  return days;
}
